import { resolveEnrollmentEffectiveDate } from "@shared/utils/effectiveDate";

type OeWindowLike = {
  startDate?: string | null;
  selectedPlanIds?: number[] | null;
} | null | undefined;

type PlanLike = {
  id: number | string;
  effectiveDate?: string | Date | null;
  renewalDate?: string | Date | null;
};

/**
 * The plan year an Open Enrollment window enrolls into — the year coverage
 * elected in this OE starts, NOT the year the OE window opens (an OE in
 * Oct 2026 for plans starting 2027-01-01 is for 2027).
 *
 * Uses the shared OE effective-date resolver on the window's selected plans
 * (client-plan dates; all given plans when the window has no selection) and
 * takes the earliest resolvable start. Falls back to the OE start date's year
 * when no plan resolves, else null.
 */
export function getOpenEnrollmentPlanYear(
  oeWindow: OeWindowLike,
  plans: PlanLike[] | null | undefined,
  now: Date = new Date(),
): string | null {
  const selectedIds = (oeWindow?.selectedPlanIds ?? []).map(Number);
  const oePlans = (plans ?? []).filter(
    (p) => selectedIds.length === 0 || selectedIds.includes(Number(p.id)),
  );

  const starts = oePlans
    .map((plan) => resolveEnrollmentEffectiveDate({ enrollmentState: "open_enrollment", plan, now }))
    .filter((d): d is string => Boolean(d))
    .sort();

  if (starts.length > 0) return starts[0].slice(0, 4);
  return oeWindow?.startDate ? String(oeWindow.startDate).split("T")[0].split("-")[0] : null;
}
